import { useState, useCallback, useRef, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ThreeScene } from "./three-scene";
import {
  Button,
  TextInput,
  Checkbox,
  Modal,
  Stack,
  Group,
  Text,
  Box,
  Paper,
  Center,
  Loader,
  Alert
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useConfig } from "../contexts/config-context";
import { useUserStore } from "../stores/userStore";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const initialParams = {
  radius: 3.5,
  detail: 1,
  wobbleStrength: 0.1,
  wobbleSpeed: 1.0,
  rotSpeedX: 0.3,
  rotSpeedY: 0.4,
  rotSpeedZ: 0.2,
  color: "#00C2A9",
  emissive: "#009494",
  emissiveIntensity: .494,
  coreGlowMode: "pulse",
  wobbleEnabled: true,
  rotationEnabled: true,
  metalness: 0.1,
  roughness: 0.1,
  transmission: 1.0,
  thickness: 3.0,
  ior: 1.45,
  opacity: 1.0,
  evolutionSpeed: 2.0,
  lightColor: "#00ffff",
  lightIntensity: 5.0,
  outerRadius: 7.5,
  outerColor: "#ffffff",
  outerEmissive: "#004466",
  outerEmissiveIntensity: 0.05,
  outerOpacity: 0.03,
  outerThickness: 0.5,
  outerRoughness: 0.01,
  outerTransmission: 0.99,
  outerIor: 1.45,
  outerEnvMapIntensity: 2.0,
  outerShellEdgeOpacity: 0.3,
  sceneBgColor: "#001f2f",
  fogDensity: 0.02,
  toneMappingExposure: 1.2,
  bloomStrength: 1.0,
  bloomRadius: 0.8,
  bloomThreshold: 0.2,
  filmGrain: 0.05,
  outerNetColor: "#00ffff",
  outerNetOpacity: 0.1,
  bgPointsColor: "#bfe9ff",
  bgPointsSize: 0.15,
  bgStarsEnabled: true,
  bgStarCount: 600,
  shootingStarsEnabled: true,
  shootingStarFrequency: 1.0,
  glowEnabled: false,
  glowCount: 5,
  glowColor: "#00e5ff",
  glowSize: 34,
  glowSoftness: 0.6,
  glowOpacity: 0.28,
  glowSpeed: 0.4,
  streamColor: "#003300",
  particleColor: "#aaffff",
  packetColor: "#ffffff",
  innerWireframeColor: "#ffffff",
  innerWireframeOpacity: 0.2,
  outerShellEdgeColor: "#88ddff",
  cameraFov: 75,
  cameraDistance: 15,
  cameraAutoRotate: false,
  cameraAutoRotateSpeed: 1.0,
  cameraEnableZoom: true,
  cameraEnablePan: false,
  cameraDamping: true,
  cameraFrameDrag: true,
  cameraLocked: false,
  cameraAzimuth: 0,
  cameraPolar: 1.5708,
  cameraTargetX: 0,
  cameraTargetY: 0,
  cameraTargetZ: 0,
};

export function GeneratorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const threeSceneRef = useRef(null);
  const { guiContainerRef, registerSaveHandler, registerUpdateParamsHandler } = useConfig();
  const { user, token } = useUserStore();

  const [params, setParams] = useState(null);
  const [initialLoadedParams, setInitialLoadedParams] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    status: 'AWAITING INPUT...',
    capacity: 0,
    nodes: 0,
    dataFlow: 0,
    integrity: 0,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [variantName, setVariantName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [loadedVariant, setLoadedVariant] = useState(null);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    const variantId = searchParams.get('variantId');

    const convertHexToString = (value) => {
      if (typeof value === 'number') {
        return '#' + value.toString(16).padStart(6, '0');
      }
      return value;
    };

    const loadVariant = async () => {
        if (variantId && user) {
            try {
                const response = await fetch(`${API_URL}/api/variants/${variantId}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                });
                if (!response.ok) {
                    throw new Error('Failed to fetch variant');
                }
                const variantData = await response.json();
                setLoadedVariant(variantData);
                setVariantName(variantData.name);
                setIsPublic(variantData.isPublic);
                setIsOwner(variantData.userId === user.id);

                const loadedParams = variantData.params;

                const colorFields = ['color', 'emissive', 'lightColor', 'outerColor', 'outerEmissive', 'sceneBgColor', 'outerNetColor', 'bgPointsColor', 'streamColor', 'particleColor', 'packetColor', 'innerWireframeColor', 'outerShellEdgeColor'];
                colorFields.forEach(field => {
                  if (loadedParams[field] !== undefined) {
                    loadedParams[field] = convertHexToString(loadedParams[field]);
                  }
                });

                const finalParams = { ...initialParams, ...loadedParams };
                setParams(finalParams);
                setInitialLoadedParams(finalParams);
                setHasChanges(false);
            } catch (e) {
                console.error("Failed to load variant from API", e);
                setLoadedVariant(null);
                setParams(initialParams);
                setInitialLoadedParams(initialParams);
            }
        } else {
            setLoadedVariant(null);
            setVariantName("");
            setIsPublic(false);
            setIsOwner(false);
            setParams(initialParams);
            setInitialLoadedParams(null);
            setHasChanges(true);
        }
    };

    loadVariant();
  }, [searchParams, user, token]);

  const handleParamsChange = useCallback((newParams) => {
    setParams(newParams);
  }, []);

  useEffect(() => {
    if (!loadedVariant) {
      setHasChanges(true);
      return;
    }

    if (initialLoadedParams && params) {
        const paramsChanged = JSON.stringify(params) !== JSON.stringify(initialLoadedParams);
        const nameChanged = variantName !== loadedVariant.name;
        const publicStatusChanged = isPublic !== loadedVariant.isPublic;
        setHasChanges(paramsChanged || nameChanged || publicStatusChanged);
    }
  }, [params, variantName, isPublic, loadedVariant, initialLoadedParams]);

  const handleStatsChange = useCallback((newStats) => {
    setStats(newStats);
  }, []);

  const openSaveModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    registerSaveHandler(openSaveModal);
  }, [registerSaveHandler, openSaveModal]);

  useEffect(() => {
    registerUpdateParamsHandler((newParams) => {
      setParams((prev) => ({ ...prev, ...newParams }));
      setHasChanges(true);
    });
  }, [registerUpdateParamsHandler]);

  const handleCreateVariant = async () => {
    if (!variantName) {
      setError("Please enter a name for the variant.");
      return;
    }
    setError(null);
    setIsSaving(true);

    const image = threeSceneRef.current?.captureScreenshot();
    const forkedFrom = loadedVariant && !isOwner ? loadedVariant.id : (loadedVariant?.forkedFromId || undefined);

    try {
      const response = await fetch(`${API_URL}/api/variants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: variantName, params, isPublic, image, forkedFrom }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save variant');
      }
      setVariantName("");
      setIsModalOpen(false);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateVariant = async () => {
    if (!variantName) {
      setError("Please enter a name for the variant.");
      return;
    }
    if (!loadedVariant || !isOwner) {
      setError("No variant loaded or you are not the owner.");
      return;
    }
    setError(null);
    setIsSaving(true);

    const image = threeSceneRef.current?.captureScreenshot();

    try {
      const response = await fetch(`${API_URL}/api/variants/${loadedVariant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: variantName, params, isPublic, image }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update variant');
      }
      setVariantName("");
      setIsModalOpen(false);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!params) {
    return (
      <Center h="100vh">
        <Loader color="cyan" />
        <Text ml="xs" c="dimmed">Loading Generator...</Text>
      </Center>
    );
  }

  return (
    <Box pos="relative" w="100%" h="100%">
      <ThreeScene
        ref={threeSceneRef}
        initialParams={params}
        onParamsChange={handleParamsChange}
        onStatsChange={handleStatsChange}
        guiContainerRef={guiContainerRef}
      />

      <Paper
        pos="absolute"
        top={16}
        left={16}
        style={{ zIndex: 10, pointerEvents: 'none' }}
        bg="rgba(0,0,0,0.3)"
        c="#00ffff"
        p="xs"
        radius="md"
        ff="monospace"
      >
        <Text size="xs" fw={700} style={{ textShadow: '0 0 5px #00ffff' }}>
          CORE :: CONFIG_MODE :: ULTIMATE
        </Text>
        <Text size="xs" fw={700} style={{ textShadow: '0 0 5px #00ffff' }}>
          STATUS: {stats.status}
        </Text>
        <Box w={200} h={4} bg="rgba(0,34,34,0.8)" mt={8} style={{ border: '1px solid rgba(0,68,68,0.8)' }}>
          <Box
            h="100%"
            bg="#00ffff"
            style={{
              width: `${stats.integrity}%`,
              boxShadow: '0 0 10px #00ffff',
              transition: 'width 100ms'
            }}
          />
        </Box>
        <Text size="xs" fw={700} mt={4} style={{ textShadow: '0 0 5px #00ffff' }}>
          CAPACITY: {stats.capacity}%
        </Text>
      </Paper>

      <Paper
        pos="absolute"
        bottom={16}
        right={16}
        style={{ zIndex: 10, pointerEvents: 'none' }}
        bg="rgba(0,0,0,0.3)"
        c="#00ffff"
        p="xs"
        radius="md"
        ff="monospace"
        ta="right"
      >
        <Text size="xs" fw={700} style={{ textShadow: '0 0 5px #00ffff' }}>
          NODES: {stats.nodes}
        </Text>
        <Text size="xs" fw={700} style={{ textShadow: '0 0 5px #00ffff' }}>
          DATA FLOW: {stats.dataFlow}/s
        </Text>
      </Paper>

      {/* Blender-tarzı kamera kısayolları */}
      <Paper
        pos="absolute"
        bottom={16}
        left={16}
        style={{ zIndex: 10, pointerEvents: 'none' }}
        bg="rgba(0,0,0,0.35)"
        c="#9fe9e2"
        p="xs"
        radius="md"
        ff="monospace"
      >
        <Text size="xs" fw={700} mb={2} c="#00ffff" style={{ textShadow: '0 0 5px #00ffff' }}>
          SHORTCUTS
        </Text>
        <Text size="xs">Sol-tık sürükle — Döndür (orbit)</Text>
        <Text size="xs">Ctrl + sol-tık — Frame offset (pan)</Text>
        <Text size="xs">Tekerlek — Zoom</Text>
        <Text size="xs">📸 Save View · 🔒 Lock (sağ panel)</Text>
      </Paper>

      <Modal
        opened={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={loadedVariant ? 'Update or Save Variant' : 'Save Variant'}
        centered
      >
        <Stack>
          <Text size="sm" c="dimmed">
            {loadedVariant ? 'You can update the current variant or save these settings as a new one.' : 'Enter a name for your new variant to save it.'}
          </Text>

          <TextInput
            label="Name"
            placeholder="My Variant"
            value={variantName}
            onChange={(e) => setVariantName(e.target.value)}
            required
          />

          <Checkbox
            label="Make this variant public for the community"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
          />

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light">
              {error}
            </Alert>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            {loadedVariant ? (
              <>
                {isOwner && (
                  <Button onClick={handleUpdateVariant} disabled={isSaving || !hasChanges} loading={isSaving}>
                    Update Current
                  </Button>
                )}
                <Button
                  onClick={handleCreateVariant}
                  disabled={isSaving || !hasChanges}
                  variant={isOwner ? "outline" : "filled"}
                  loading={isSaving}
                >
                  Save as New
                </Button>
              </>
            ) : (
              <Button onClick={handleCreateVariant} disabled={isSaving} loading={isSaving}>
                Save
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
